import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { MODEL_NEW_UNTIL, type ModelId } from "@shaarp/common";
import { useMemo } from "react";
import { LLMAdminOnlyBadge } from "./llm-admin-only-badge";

type BadgeType = "new" | "france" | "dataNotStored" | "adminOnly";

interface ModelConfig {
  badges: BadgeType[];
  /** ISO date string until which the "new" badge should be displayed */
  newUntil?: string;
  description: string;
}

interface LLMModelCardDescriptionProps {
  modelId: string;
}

export function LLMModelCardDescription({
  modelId,
}: LLMModelCardDescriptionProps) {
  const { t } = useTranslation();

  // `newUntil` comes from MODEL_NEW_UNTIL (@shaarp/common), the same constant
  // the API catalogue is pinned against — a literal copied here could keep
  // badging a model as new long after the API stopped calling it that.
  const llmModelsConfigs: Record<ModelId, ModelConfig> = {
    "openai/gpt-6.1-sol": {
      badges: ["new", "dataNotStored"],
      newUntil: MODEL_NEW_UNTIL["openai/gpt-6.1-sol"],
      description: t("team.components.llmPermissions.descriptions.gpt6Sol"),
    },
    "mistral/mistral-large-4": {
      badges: ["france", "dataNotStored"],
      description: t(
        "team.components.llmPermissions.descriptions.mistralMedium35"
      ),
    },
    "anthropic/claude-sonnet-5-5": {
      badges: ["new", "dataNotStored"],
      newUntil: MODEL_NEW_UNTIL["anthropic/claude-sonnet-5-5"],
      description: t(
        "team.components.llmPermissions.descriptions.claudeSonnet55"
      ),
    },
  };

  const config = llmModelsConfigs[modelId as ModelId];

  const filteredBadges = useMemo(() => {
    if (!config) return [];

    return config.badges.filter((badge) => {
      if (badge === "new") {
        return config.newUntil && new Date(config.newUntil) > new Date();
      }
      return true;
    });
  }, [config]);

  if (!config) {
    return null;
  }

  return (
    <>
      {/* Badges */}
      {filteredBadges.length > 0 && (
        <div className="flex flex-wrap w-full gap-1.5 pb-3">
          {filteredBadges.map((badge) => (
            <ModelBadge key={badge} badge={badge} />
          ))}
        </div>
      )}

      {/* Description */}
      <div className="pt-2 text-xs leading-relaxed text-muted-foreground">
        {config.description}
      </div>
    </>
  );
}

function ModelBadge({ badge }: { badge: BadgeType }) {
  const { t } = useTranslation();

  if (badge === "adminOnly") {
    return <LLMAdminOnlyBadge />;
  }

  const llmModelsBadgesConfigs = {
    new: {
      variant: "success" as const,
      text: t("team.components.llmPermissions.badges.new"),
      tooltip: null,
    },
    france: {
      variant: "default" as const,
      text: t("team.components.llmPermissions.badges.france"),
      tooltip: t("team.components.llmPermissions.tooltip.madeInFrance"),
    },
    dataNotStored: {
      variant: "secondary" as const,
      text: t("team.components.llmPermissions.badges.dataNotStored"),
      tooltip: null,
    },
  };

  const config = llmModelsBadgesConfigs[badge];

  const badgeElement = (
    <Badge variant={config.variant} className="text-xs px-2 py-0.5 font-medium">
      {config.text}
    </Badge>
  );

  if (config.tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badgeElement}</TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">{config.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badgeElement;
}
